import { Component, inject, OnInit, signal } from '@angular/core';
import { HomeAssetsComponent } from './components/home-assets/home-assets.component';
import { switchMap } from 'rxjs';
import { WalkThroughService } from '@client-monorepo/shared/common/walk-through';
import { WealthNavigationService } from '@client-monorepo/wealth/navigation';

import { NgxEventTrackerService } from '@digipay/ngx-event-tracker';
import { NgxAppBarComponent } from '@digipay/ngx-app-bar';
import { BaseComponent } from '../../components/core/components/base/base.component';
import { EIntrackEventName } from '../../components/core/models/intrack-event-name.enum';
import { FundDataService } from '../../components/core/services/fund-data.service';
import { LeadService } from '../../components/core/services/lead.service';
import { ProfileService } from '../../components/core/services/profile.service';
import { checkWealthOrigin } from '../../components/utils/check-wealth-origin';
import { WEALTH_TOKEN } from '../../components/utils/variables';
import {
  CAMPAIGN_OTP_ROUTE,
  CROWD_LIST_ROUTE,
  INVESTMENT_LIST_ROUTE,
  PROFILE_ROUTE,
  TRANSACTIONS_ROUTE,
  TREASURE_HUNT_CAPACITY_FULL_ROUTE,
  TREASURE_HUNT_NATIONAL_ID_ROUTE,
  TREASURE_HUNT_REGISTRATION_SUCCESSFUL_ROUTE,
  TREASURE_HUNT_START_ROUTE,
  TREASURE_HUNT_SUCCESSFUL_ROUTE,
  WALLETS_ROUTE,
} from '../../data-access/constants/app-routes';
import { CoordinatorPage } from '../../data-access/enums/coordinator-page';
import { TokenModel } from '../../data-access/models/base/token.model';
import { DashboardBanner, DashboardCategory } from '../../data-access/models/dashboard-parts.model';
import { ProfileResponse } from '../../data-access/models/profile-response.model';
import { TreasureHuntCampaignProcess } from '../../data-access/models/treasure-hunt.model';
import { BackToOriginService } from '../../shared/services/back-to-origin.service';
import { WealthWalkthroughService } from '../../shared/services/wealth-walkthrough.service';
import { IpoBannerComponent } from '../ipo/components/ipo-banner/ipo-banner.component';
import { IIPOLandingBanner } from '../ipo/models/ipo-landing-banner.interface';
import { IPOService } from '../ipo/services/ipo.service';
import { HomeBannerComponent } from './components/home-banner/home-banner.component';
import { HomeCategoriesComponent } from './components/home-categories/home-categories.component';
import { HomeNewsComponent } from './components/home-news/home-news.component';
import { HomeService } from './services/home.service';
import { ICustomerPortfolio } from '../../components/core/models/customer-portfolio.interface';
import { NgClass } from '@angular/common';

@Component({
  selector: 'wealth-applet-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  standalone: true,
  imports: [
    HomeAssetsComponent,
    HomeBannerComponent,
    HomeCategoriesComponent,
    HomeNewsComponent,
    IpoBannerComponent,
    NgxAppBarComponent,
    NgClass,
  ],
})
export class HomeComponent extends BaseComponent implements OnInit {
  loadingAssets = signal<boolean>(true);
  isWebsite = signal<boolean>(true);
  userProfile = signal<ProfileResponse | undefined>(undefined);
  assetData = signal<ICustomerPortfolio | undefined>(undefined);

  wealthBanner = signal<DashboardBanner[]>([
    {
      iconPath: 'wealth-assets/images/golbarg-banner.png',
      path: INVESTMENT_LIST_ROUTE,
      bannerId: '11421',
    },
  ]);

  wealthSecondBanner = signal<DashboardBanner[]>([
    {
      iconPath: 'wealth-assets/images/wealth-banner.png',
      path: INVESTMENT_LIST_ROUTE,
      queryParams: 'FixedIncome',
    },
  ]);
  dpBanners = signal<DashboardBanner[]>([
    {
      iconPath: 'wealth-assets/images/home-slider/BANNER_GOLBARG.png',
      path: INVESTMENT_LIST_ROUTE,
      bannerId: '11421',
      title: 'KARDAN',
    },
    {
      iconPath: 'wealth-assets/images/home-slider/TREASURY.webp',
      path: WALLETS_ROUTE,
      bannerId: 'treasury',
      title: 'TREASURY',
    },
    {
      iconPath: 'wealth-assets/images/home-slider/BANNER_KIMIA_ZARRIN_KARDAN.png',
      path: INVESTMENT_LIST_ROUTE,
      bannerId: 'IRTKGANJ0001',
      title: 'KARDAN',
    },
  ]);

  dpSecondBanners = signal<DashboardCategory[]>([
    {
      iconPath: 'wealth-assets/images/gold-card.png',
      path: INVESTMENT_LIST_ROUTE,
      query: 'Gold',
      id: 'GOLD',
      text: 'سرمایه‌گذاری مبتنی بر طلا',
    },
    {
      iconPath: 'wealth-assets/images/mutual-fund-card.png',
      path: INVESTMENT_LIST_ROUTE,
      query: 'FixedIncome',
      id: 'FIXED_INCOME',
      text: 'صندوق‌های درامد ثابت',
    },
    {
      iconPath: 'wealth-assets/images/index-fund-card.png',
      path: INVESTMENT_LIST_ROUTE,
      query: 'Index',
      text: 'صندوق‌های شاخصی',
      id: 'INDEX_FUND',
      comingSoon: false,
    },
    {
      iconPath: 'wealth-assets/images/crowd-card.png',
      path: CROWD_LIST_ROUTE,
      text: 'تامین مالی جمعی',
      id: 'CROWD',
      comingSoon: false,
    },
  ]);

  ipoBanner = signal<IIPOLandingBanner | undefined>(undefined);
  campaignBanner = signal<TreasureHuntCampaignProcess[] | undefined>(undefined);
  ipoBannerLoading = signal<boolean>(true);
  private navigationService = inject(WealthNavigationService);
  private leadService = inject(LeadService);
  private fundService = inject(FundDataService);
  private profileService = inject(ProfileService);
  private backToOrigin = inject(BackToOriginService);
  private iPOService = inject(IPOService);
  public walkThroughService = inject(WalkThroughService);
  private wealthWalkthroughService = inject(WealthWalkthroughService);
  private homeService = inject(HomeService);
  private eventService = inject(NgxEventTrackerService);
  isLoadingNewData = signal<boolean>(false);

  constructor() {
    super();
  }

  ngOnInit() {
    const origin = checkWealthOrigin();
    this.isWebsite.set(origin === 'wealth');
    this.getData();
    this.profileService
      .getProfile()
      .pipe(
        switchMap((profile) => {
          this.userProfile.set(profile?.result);
          return this.fundService.getCustomerPortfolio();
        }),
      )
      .subscribe((res) => {
        if (res) {
          this.assetData.set(res.result);
        }
        this.loadingAssets.set(false);

        if (this.userProfile() && this.userProfile().onboardedSections) {
          this.wealthWalkthroughService.startWalkthrough(this.userProfile().onboardedSections);
        }
      });

    this.iPOService.getLandingBanner().subscribe((banner) => {
      if (banner?.success) {
        this.ipoBanner.set(banner.result);
      }

      this.ipoBannerLoading.set(false);
    });

    this.homeService.getCampaignBanner().subscribe((banner) => {
      if (banner?.success) {
        this.campaignBanner.set(banner.result.campaigns);
      }
    });

    this.eventService.sendEvent({ eventName: EIntrackEventName.HOME_PAGE_VIEW, eventData: {} });
  }

  onBackHandler() {
    this.backToOrigin.goBackToOrigin();
  }

  onCampaignBannerClick(campaign: TreasureHuntCampaignProcess) {
    this.homeService.getCampaignProcess(campaign.code, 'action=start_journey').subscribe((res) => {
      if (res?.success) {
        switch (res.result.data.pageName) {
          case CoordinatorPage.PAGE_CAMPAIGN_CAPACITY_REACHED:
            this.navigationService.navigate([TREASURE_HUNT_CAPACITY_FULL_ROUTE]);
            break;
          case CoordinatorPage.PAGE_CAMPAIGN_PRIZE_WAITING:
            this.navigationService.navigateWithState([TREASURE_HUNT_REGISTRATION_SUCCESSFUL_ROUTE], {
              state: {
                remainingDays: res.result.data.remainingDays,
              },
            });
            break;
          case CoordinatorPage.PAGE_CAMPAIGN_GUEST_PRIZE_BEGINNING:
            this.navigationService.navigateWithState([TREASURE_HUNT_START_ROUTE], {
              state: {
                phoneNumber: res.result.data.phoneNumber,
                role: 'guest',
                campaignCode: campaign.code,
              },
            });
            break;
          case CoordinatorPage.PAGE_CAMPAIGN_CUSTOMER_PRIZE_BEGINNING:
            this.navigationService.navigateWithState([TREASURE_HUNT_START_ROUTE], {
              state: {
                phoneNumber: res.result.data.phoneNumber,
                role: 'customer',
                campaignCode: campaign.code,
              },
            });
            break;
          case CoordinatorPage.PAGE_CAMPAIGN_PRIZE_RECEIVED:
            this.navigationService.navigateWithState([TREASURE_HUNT_SUCCESSFUL_ROUTE], {
              state: {
                prizeAmount: res.result.data.prizeAmount,
                symbol: res.result.data.instrumentSymbol,
                instrumentName: res.result.data.instrumentDisplaySymbol,
              },
            });
            break;
          case CoordinatorPage.PAGE_CAMPAIGN_OTP:
            this.navigationService.navigateWithState([CAMPAIGN_OTP_ROUTE], {
              state: {
                campaignCode: campaign.code,
                countDown: res.result.data.countdownInSeconds,
              },
            });
            break;
          default:
            this.navigationService.navigate([TREASURE_HUNT_NATIONAL_ID_ROUTE]);
            break;
        }
      }
    });
  }

  private getData() {
    const wealthToken: TokenModel = JSON.parse(localStorage.getItem(WEALTH_TOKEN));
    if (wealthToken?.accessToken) {
      this.leadService.getLead().subscribe();
    }
  }

  goToTransactions() {
    this.navigationService.navigate([TRANSACTIONS_ROUTE]);
  }

  goToProfile() {
    this.navigationService.navigate([PROFILE_ROUTE]);
  }

  getUpdatedAssets() {
    this.isLoadingNewData.set(true);
    this.fundService.getCustomerPortfolio(true).subscribe((res) => {
      if (res.success) {
        {
          this.assetData.set(res.result);
        }
      }
      this.isLoadingNewData.set(false);
    });
  }
}
